import { css, html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators/property.js";
import { classMap } from "lit/directives/class-map.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { repeat } from "lit/directives/repeat.js";
import { state } from "lit/decorators/state.js";
import debounce from "lodash-es/debounce";
import { postChainEligibleNonprofitsForCart, updateSubscription } from "../../api-sdk/v3/routes";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { AsyncController } from "../../shared/controllers/async-controller";
import {
  DEFAULT_BASE_URL,
  LOCAL_STORAGE_KEYS,
  SELECTION_METHODS,
  SUPPORTED_LANGUAGES,
  WIDGET_NAMES,
} from "../../api-sdk/constants";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { _loading } from "../../shared/fragments/loading-template";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { cssReset } from "../../shared/css-reset";
import { makeApiKeyHeader } from "../../utils/makeApiKeyHeader";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { BeamError, MissingConfig } from "../../utils/beam-errors";
import { createScopedLocalStorage } from "../../utils/local-storage";
import { cssCardGrid } from "../../shared/fragments/css-card-grid";
import { _checkbox } from "../../shared/fragments/checkbox";
import { BeamNonprofitSelectEvent } from "../../utils/events";
import { localizeUserString } from "../../shared/localize";
import { TNumericId } from "../../shared/types";
import { LANGUAGES } from "../../api-sdk/types";
import { enforceConfig } from "../../shared/enforce-config";
import { strings } from "./strings";
import "../beam-partner-logos";

interface SubscriptionRequiredConfig {
  apiKey: string;
  subscriptionId: string;
  storeId: TNumericId;
}
const cacheTtl = 2 * 60 * 60 * 1000;
export class BeamSelectSubscriptionNonprofit extends LitElement {
  static tagName = "beam-select-subscription-nonprofit";

  @property({ type: String }) public baseUrl: string = DEFAULT_BASE_URL;
  @property({ type: String }) public apiKey?: SubscriptionRequiredConfig["apiKey"];
  @property({ type: String }) public subscriptionId?: SubscriptionRequiredConfig["subscriptionId"];
  @property({ type: Number }) public storeId?: SubscriptionRequiredConfig["storeId"];
  @property({ type: String }) public countryCode?: string;
  @property({ type: String }) public postalCode?: string;
  @property({ type: Number, reflect: true }) public selectedNonprofitId?: TNumericId;
  @property({ type: String }) public lang: LANGUAGES = "en";
  @property({ type: Boolean }) public draftConfig = false;
  @property({ type: Boolean }) public debug = false;
  @state() private isMobile = window.innerWidth < 768;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  private getChainNonprofits = async () => {
    if (!enforceConfig<SubscriptionRequiredConfig>(["apiKey", "subscriptionId", "storeId"], this)) {
      throw new MissingConfig();
    }
    const res = await postChainEligibleNonprofitsForCart({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        storeId: this.storeId,
        postalCode: this.postalCode,
        countryCode: this.countryCode,
        widgetName: "select-subscription-nonprofit",
        version: "1.0.0",
        lang: this.configLang,
        options: {
          config: {
            draftConfig: this.draftConfig,
          },
        },
      },
    });

    this.localStorage.setItemJson(LOCAL_STORAGE_KEYS.CHAIN_NONPROFITS, {
      createdAt: new Date(),
      data: res,
    });

    return res;
  };

  private updateNonprofitIdForSubscription = async ({ selectedNonprofitId }: { selectedNonprofitId: TNumericId }) => {
    if (!this.subscriptionId) {
      return null;
    }

    if (!enforceConfig<SubscriptionRequiredConfig>(["apiKey", "subscriptionId"], this)) {
      throw new MissingConfig();
    }

    return updateSubscription({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      requestBody: {
        remoteSubscriptionId: this.subscriptionId,
        nonprofitId: selectedNonprofitId,
        selectionMethod: SELECTION_METHODS.subscription_management_widget,
      },
    });
  };

  private nonprofitListDataController = new AsyncController<typeof this.getChainNonprofits>(
    this,
    this.getChainNonprofits,
  );

  private subscriptionUpdateDataController = new AsyncController<typeof this.updateNonprofitIdForSubscription>(
    this,
    this.updateNonprofitIdForSubscription,
  );

  private localStorage = createScopedLocalStorage(this as LitElement & SubscriptionRequiredConfig);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.evaluateBreakPoints);
  }

  async firstUpdated(_changedProperties: PropertyValues) {
    await this.restoreStateFromCache();
  }

  async updated(previousPropertyValues: PropertyValues) {
    // Reload nonprofit list on change of any of these props:
    // Also fires on first load as props go from undefined => value
    const requireNewDataProps = ["baseUrl", "storeId", "apiKey", "countryCode", "postalCode", "lang"];
    for (const prop of requireNewDataProps) {
      if (previousPropertyValues.has(prop)) {
        await this.nonprofitListDataController.exec();
        break;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private restoreStateFromCache() {
    if (!enforceConfig<SubscriptionRequiredConfig>(["apiKey"], this)) throw new MissingConfig();

    try {
      //   try to restore nonprofit list if we have data and it's not too old
      //   list will continue to refresh async and replace this data when ready
      const { createdAt = 0, data } =
        this.localStorage.getItemJson<{ createdAt: string; data: any }>(LOCAL_STORAGE_KEYS.CHAIN_NONPROFITS) || {};
      if (new Date(createdAt).valueOf() + cacheTtl > new Date().valueOf()) {
        this.nonprofitListDataController.data = data;
        this.nonprofitListDataController.loading = false;
      }
    } catch (err) {
      // ignore cache retrieval error and continue to fetch data
    }
  }

  /**
   * Factory for selection event handler
   *
   * Nonprofit selector implements radio-button semantics:
   * * If nothing is selected, tabbing into selector selects first card
   * * Arrow keys changes focus between cards, but doesn't select
   * * Enter/Space sets selection
   * * If a nonprofit is selected, arrow keys change focus AND selection
   * * Click sets selection
   * @param {number} id
   * @param {number} index
   * @param {{id: number}[]} nonprofits
   * @returns {(evt: Event) => void}
   */
  private makeHandleSelect =
    (id: number, index: number, nonprofits: { nonprofit: { id: number } }[]): ((event: Event) => void) =>
    async (event: Event) => {
      if (!this.allowChange) return;

      const currentId = this.selectedNonprofitId;
      if (event instanceof KeyboardEvent) {
        let nextFocus = null;
        switch (event.key) {
          case "ArrowUp":
          case "ArrowLeft":
            if (index === 0) {
              nextFocus = nonprofits[nonprofits.length - 1];
            } else {
              nextFocus = nonprofits[index - 1];
            }
            event.preventDefault();
            break;
          case "ArrowRight":
          case "ArrowDown":
            if (index === nonprofits.length - 1) {
              nextFocus = nonprofits[0];
            } else {
              nextFocus = nonprofits[index + 1];
            }
            event.preventDefault();
            break;
          case "Enter":
          case " ":
            event.preventDefault();
            break; // continue to toggle-selection block below
          default:
            return;
        }
        if (nextFocus) {
          const focusTarget = this.renderRoot.querySelector(`[data-value="${nextFocus.nonprofit.id}"]`) as HTMLElement;
          if (focusTarget !== null) {
            focusTarget.tabIndex = 0;
            focusTarget.focus();
          }
          return;
        }
      }
      // Handle selection with click or Enter/Space key
      const targetEl = event.currentTarget;
      if (targetEl instanceof HTMLElement) {
        if (currentId === id) {
          // this["selected-nonprofit-id"] = undefined; // unset (not supported)
          return; // no API call or localStorage change needed
        } else {
          this.selectedNonprofitId = id;
        }
      }
    };

  get allowChange() {
    // Disallow change if already submitted
    return this.subscriptionUpdateDataController.data == null && !this.subscriptionUpdateDataController.loading;
  }

  get allowSubmit() {
    // Disabled if no nonprofit has been selected yet or if submission was already received
    return Boolean(this.selectedNonprofitId && this.allowChange);
  }

  handleSubmit = async () => {
    const { selectedNonprofitId } = this;
    if (!this.allowSubmit || selectedNonprofitId == null) return;
    await this.subscriptionUpdateDataController.exec({ selectedNonprofitId });

    const selectedNonprofit = this.nonprofitListDataController?.data?.nonprofits.find(
      (np) => np.nonprofit.id === selectedNonprofitId,
    );

    this.dispatchEvent(
      new BeamNonprofitSelectEvent({
        selectedNonprofitId,
        nonprofitName: selectedNonprofit?.nonprofit.name,
        source: WIDGET_NAMES.select_subscription_nonprofit,
        timestamp: new Date(),
      }),
    );
  };

  evaluateBreakPoints = debounce(
    () => {
      this.isMobile = window.innerWidth < 768;
    },
    50,
    { maxWait: 50, leading: true },
  );

  public get cssVariables() {
    const defaults = {
      ...progressBarConfigDefaults,
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "inherit",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      "--beam-SelectSubscriptionNonprofit-maxWidth": "800px",
      "--beam-SelectSubscriptionNonprofit-mobile-header-alignItems": "center",
      "--beam-SelectSubscriptionNonprofit-desktop-header-alignItems": "center",
      "--beam-SelectSubscriptionNonprofit-headerContainer-padding": "2px",
      "--beam-SelectSubscriptionNonprofit-title-textAlign": "inherit",
      "--beam-SelectSubscriptionNonprofit-description-textAlign": "center",
      "--beam-SelectSubscriptionNonprofit-description-lineHeight": "138%",
      "--beam-SelectSubscriptionNonprofit-description-marginTop": "3px",
      "--beam-SelectSubscriptionNonprofit-desktop-description-width": "80%",
      "--beam-SelectSubscriptionNonprofit-mobile-description-width": "90%",
      "--beam-SelectSubscriptionNonprofit-options-marginTop": "10px",
      "--beam-SelectSubscriptionNonprofit-options-padding": "10px",
      "--beam-SelectSubscriptionNonprofit-options-borderWidth": "1px",
      "--beam-SelectSubscriptionNonprofit-options-borderColor": "transparent",
      "--beam-SelectSubscriptionNonprofit-options-borderRadius": "8px",
      "--beam-SelectSubscriptionNonprofit-options-backgroundColor": "#F9FBFB",
      "--beam-SelectSubscriptionNonprofit-option-padding": "15px",
      "--beam-SelectSubscriptionNonprofit-options-hover-backgroundColor": "#e1f1ee",
      "--beam-SelectSubscriptionNonprofit-options-hover-borderWidth": "1px",
      "--beam-SelectSubscriptionNonprofit-options-hover-borderColor": "#e1f1ee",
      "--beam-SelectSubscriptionNonprofit-options-selected-borderWidth": "1px",
      "--beam-SelectSubscriptionNonprofit-options-selected-borderColor": "#e1f1ee",
      "--beam-SelectSubscriptionNonprofit-options-selected-backgroundColor": "#e1f1ee",
      "--beam-SelectSubscriptionNonprofit-options-gap": "20px",
      "--beam-SelectSubscriptionNonprofit-options-minWidth": "250px",
      "--beam-SelectSubscriptionNonprofit-options-columnCount": "2",
      "--beam-SelectSubscriptionNonprofit-options-icon-borderRadius": "4px",
      "--beam-SelectSubscriptionNonprofit-options-icon-padding": "10px",
      "--beam-SelectSubscriptionNonprofit-options-image-borderRadius": "4px",
      "--beam-SelectSubscriptionNonprofit-cardIconContainer-height": "100%",
      "--beam-SelectSubscriptionNonprofit-iconBackground-width": "50px",
      "--beam-SelectSubscriptionNonprofit-iconBackground-height": "50px",
      "--beam-SelectSubscriptionNonprofit-iconBackground-borderRadius": "50%",
      "--beam-SelectSubscriptionNonprofit-iconBackground-background": "#0a323c",
      "--beam-SelectSubscriptionNonprofit-icon-height": "50px",
      "--beam-SelectSubscriptionNonprofit-checkmark-backgroundColor": "transparent",
      "--beam-SelectSubscriptionNonprofit-checkmark-color": "transparent",
      "--beam-SelectSubscriptionNonprofit-checkmark-borderColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-checkmark-selected-color": "#fff",
      "--beam-SelectSubscriptionNonprofit-checkmark-selected-backgroundColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-checkmark-selected-borderColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-checkmark-hover-color": "#fff",
      "--beam-SelectSubscriptionNonprofit-checkmark-hover-backgroundColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-checkmark-hover-borderColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-cardStyle": "'selected_icon'",
      "--beam-SelectSubscriptionNonprofit-submitButton-color": "#fff",
      "--beam-SelectSubscriptionNonprofit-submitButton-hover-color": "#fff",
      "--beam-SelectSubscriptionNonprofit-submitButton-disabled-color": "#fff",
      "--beam-SelectSubscriptionNonprofit-submitButton-backgroundColor": "#0A323C",
      "--beam-SelectSubscriptionNonprofit-submitButton-hover-backgroundColor": "#222",
      "--beam-SelectSubscriptionNonprofit-submitButton-disabled-backgroundColor": "#767676",
      "--beam-SelectSubscriptionNonprofit-submitButton-borderColor": "#333",
      "--beam-SelectSubscriptionNonprofit-submitButton-hover-borderColor": "#222",
      "--beam-SelectSubscriptionNonprofit-submitButton-disabled-borderColor": "#767676",
      "--beam-SelectSubscriptionNonprofit-submitButton-fontSize": "14px",
      "--beam-SelectSubscriptionNonprofit-submitButton-fontWeight": "inherit",
      "--beam-SelectSubscriptionNonprofit-submitButton-fontFamily": "inherit",
      "--beam-SelectSubscriptionNonprofit-submitButton-textTransform": "none",
      "--beam-SelectSubscriptionNonprofit-submitButton-borderRadius": "30px",
      "--beam-SelectSubscriptionNonprofit-submitButton-padding": "10px 30px",
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-title", {
        fontSize: "20px",
        fontWeight: "bold",
        lineHeight: "120%",
        textTransform: "capitalize",
        color: "#0a323c",
      }),
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-descriptionPrefix", {
        marginTop: "3px",
        color: "#2f302d",
        fontSize: "14px",
        fontWeight: "bold",
        fontStyle: "normal",
        lineHeight: "138%",
      }),
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-description", {
        color: "#2f302d",
        fontSize: "14px",
        fontStyle: "normal",
      }),
      "--beam-SelectSubscriptionNonprofit-details-cause-letterSpacing": "0.36px",
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-details-cause", {
        fontSize: "12px",
        fontWeight: "bold",
        color: "#0a323c",
        fontStyle: "normal",
        lineHeight: "120%",
      }),
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-details-description", {
        fontSize: "12px",
        marginTop: "5px",
        fontStyle: "normal",
        lineHeight: "140%",
        fontWeight: "400",
      }),
      "--beam-SelectSubscriptionNonprofit-details-fundingProgress-marginTop": "7px",
      ...defineCustomText("--beam-SelectSubscriptionNonprofit-details-funding-progress-label", {
        fontSize: "12px",
        color: "#666666",
        fontWeight: "400",
        fontStyle: "normal",
      }),
      "--beam-SelectSubscriptionNonprofit-stickyFooter-boxShadow": "0px -20px 20px 0px rgba(0, 0, 0, 0.04)",
      "--beam-SelectSubscriptionNonprofit-stickyFooter-padding": "15px 0px 45px",
      "--beam-SelectSubscriptionNonprofit-stickyFooter-backgroundColor": "white",
      "--beam-SelectSubscriptionNonprofit-stickyFooter-marginLeft": "-15px",
      "--beam-SelectSubscriptionNonprofit-stickyFooter-marginRight": "-15px",
      "--beam-SelectSubscriptionNonprofit-stickyFooter-width": "auto",
    };

    const remoteConfig = this.nonprofitListDataController?.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    css`
      :host {
        display: block;
        max-width: var(--beam-SelectSubscriptionNonprofit-maxWidth);
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        word-break: normal;
      }

      .widget-container-mobile {
        position: relative;
      }

      .header-container {
        align-items: flex-start;
        justify-content: space-between;
        padding: var(--beam-SelectSubscriptionNonprofit-headerContainer-padding);
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .header-container-mobile {
        align-items: var(--beam-SelectSubscriptionNonprofit-mobile-header-alignItems);
      }

      .header-container-desktop {
        align-items: var(--beam-SelectSubscriptionNonprofit-desktop-header-alignItems);
      }

      .title {
        text-align: var(--beam-SelectSubscriptionNonprofit-title-textAlign);
        ${useCustomText("--beam-SelectSubscriptionNonprofit-title")};
      }

      .description-container {
        text-align: var(--beam-SelectSubscriptionNonprofit-description-textAlign);
        line-height: var(--beam-SelectSubscriptionNonprofit-description-lineHeight);
        margin-top: var(--beam-SelectSubscriptionNonprofit-description-marginTop);
      }

      .description-prefix {
        ${useCustomText("--beam-SelectSubscriptionNonprofit-descriptionPrefix")};
      }

      .description {
        ${useCustomText("--beam-SelectSubscriptionNonprofit-description")};
      }

      .description-desktop {
        width: var(--beam-SelectSubscriptionNonprofit-desktop-description-width);
      }

      .description-mobile {
        width: var(--beam-SelectSubscriptionNonprofit-mobile-description-width);
      }

      .root {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex-shrink: 0;
      }

      .options {
        margin-top: var(--beam-SelectSubscriptionNonprofit-options-marginTop);
        padding: var(--beam-SelectSubscriptionNonprofit-options-padding);
      }

      .card-icon-container {
        height: var(--beam-SelectSubscriptionNonprofit-cardIconContainer-height);
        flex: 0;
        flex-direction: column;
      }

      .icon-background {
        width: var(--beam-SelectSubscriptionNonprofit-iconBackground-width);
        height: var(--beam-SelectSubscriptionNonprofit-iconBackground-height);
        border-radius: var(--beam-SelectSubscriptionNonprofit-iconBackground-borderRadius);
        overflow: hidden;
        background: var(--beam-SelectSubscriptionNonprofit-iconBackground-background);
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .card-icon {
        height: var(--beam-SelectSubscriptionNonprofit-icon-height);
        display: block;
        padding: var(--beam-SelectSubscriptionNonprofit-options-icon-padding);
        border-radius: var(--beam-SelectSubscriptionNonprofit-options-icon-borderRadius);
      }

      .card-image-container {
        height: 100%;
        flex: 4;
        display: flex;
        flex-direction: column;
      }

      .card-image {
        flex: 1;
        height: auto;
        width: 100%;
        object-fit: cover;
        border-radius: var(--beam-SelectSubscriptionNonprofit-options-image-borderRadius);
      }

      .option {
        /* outline is used to prevent layer shift if changing border width */
        outline: var(--beam-SelectSubscriptionNonprofit-options-borderWidth) solid
          var(--beam-SelectSubscriptionNonprofit-options-borderColor);
        border-radius: var(--beam-SelectSubscriptionNonprofit-options-borderRadius);
        background-color: var(--beam-SelectSubscriptionNonprofit-options-backgroundColor);
        cursor: pointer;
        transition:
          border-color 0.2s,
          background-color 0.2s,
          outline 0.2s;
        display: flex;
        gap: 15px;
        padding: var(--beam-SelectSubscriptionNonprofit-option-padding);
      }

      .options.cardGrid {
        order: 3;
      }

      .option:not(.isDisabled):focus-visible,
      .option:not(.isDisabled):hover,
      .option:not(.isSelected):focus-visible,
      .option:not(.isSelected):hover {
        background-color: var(--beam-SelectSubscriptionNonprofit-options-hover-backgroundColor);
        outline: var(--beam-SelectSubscriptionNonprofit-options-hover-borderWidth) solid
          var(--beam-SelectSubscriptionNonprofit-options-hover-borderColor);
      }

      .option.isDisabled {
        cursor: not-allowed;
      }

      .option.isSelected {
        outline: var(--beam-SelectSubscriptionNonprofit-options-selected-borderWidth) solid
          var(--beam-SelectSubscriptionNonprofit-options-selected-borderColor);
        background-color: var(--beam-SelectSubscriptionNonprofit-options-selected-backgroundColor);
      }

      .option.isSelected:focus-visible,
      .option.isSelected:hover {
        outline: var(--beam-SelectSubscriptionNonprofit-options-selected-borderWidth) solid
          var(--beam-SelectSubscriptionNonprofit-options-selected-borderColor);
      }

      .option .checkbox {
        margin-left: 5px;
        float: right;
        padding: 2px;
        border-radius: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-width: 1px;
        border-style: solid;
        transition:
          background-color 0.2s,
          color 0.2s,
          border-color 0.2s;
        color: var(--beam-SelectSubscriptionNonprofit-checkmark-color);
        background-color: var(--beam-SelectSubscriptionNonprofit-checkmark-backgroundColor);
        border-color: var(--beam-SelectSubscriptionNonprofit-checkmark-borderColor);
      }

      .option.isSelected .checkbox,
      .option.isSelected:focus-visible .checkbox,
      .option.isSelected:hover .checkbox {
        color: var(--beam-SelectSubscriptionNonprofit-checkmark-selected-color);
        background-color: var(--beam-SelectSubscriptionNonprofit-checkmark-selected-backgroundColor);
        border-color: var(--beam-SelectSubscriptionNonprofit-checkmark-selected-borderColor);
      }

      .option:not(.isSelected):focus-visible .checkbox,
      .option:not(.isSelected):hover .checkbox {
        color: var(--beam-SelectSubscriptionNonprofit-checkmark-hover-color);
        background-color: var(--beam-SelectSubscriptionNonprofit-checkmark-hover-backgroundColor);
        border-color: var(--beam-SelectSubscriptionNonprofit-checkmark-hover-borderColor);
      }

      .card-details-container {
        flex: 1 1 67%;
        height: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: space-between;
        align-content: stretch;
        align-items: stretch;
        position: relative;
      }

      .details-cause {
        flex: 0 1;
        ${useCustomText("--beam-SelectSubscriptionNonprofit-details-cause")};
        letter-spacing: var(--beam-SelectSubscriptionNonprofit-details-cause-letterSpacing);
      }

      .details-description {
        flex: 1 1;
        ${useCustomText("--beam-SelectSubscriptionNonprofit-details-description")}
      }

      .details-description .nonprofitName {
        font-weight: var(--beam-SelectSubscriptionNonprofit-details-nonprofitName-fontWeight);
        font-style: var(--beam-SelectSubscriptionNonprofit-details-nonprofitName-fontStyle);
      }

      .card-progress-bar {
        display: flex;
        margin-top: var(--beam-SelectSubscriptionNonprofit-details-fundingProgress-marginTop);
        align-items: center;
      }

      .details-funding-progress-label {
        white-space: nowrap;
        text-align: right;
        flex: 0 1;
        margin-left: 15px;
        ${useCustomText("--beam-SelectSubscriptionNonprofit-details-funding-progress-label")}
      }

      .footer {
        text-align: center;
      }

      .sticky-footer {
        position: sticky;
        background: var(--beam-SelectSubscriptionNonprofit-stickyFooter-backgroundColor);
        bottom: 0;
        box-shadow: var(--beam-SelectSubscriptionNonprofit-stickyFooter-boxShadow);
        padding: var(--beam-SelectSubscriptionNonprofit-stickyFooter-padding);
        left: 0;
        right: 0;
        margin-left: var(--beam-SelectSubscriptionNonprofit-stickyFooter-marginLeft);
        margin-right: var(--beam-SelectSubscriptionNonprofit-stickyFooter-marginRight);
        width: var(--beam-SelectSubscriptionNonprofit-stickyFooter-width);
      }

      .footer-desktop {
        margin-top: 20px;
      }

      .submitButton {
        color: var(--beam-SelectSubscriptionNonprofit-submitButton-color);
        background-color: var(--beam-SelectSubscriptionNonprofit-submitButton-backgroundColor);
        border: 1px solid var(--beam-SelectSubscriptionNonprofit-submitButton-borderColor);
        font-size: var(--beam-SelectSubscriptionNonprofit-submitButton-fontSize);
        font-weight: var(--beam-SelectSubscriptionNonprofit-submitButton-fontWeight);
        font-family: var(--beam-SelectSubscriptionNonprofit-submitButton-fontFamily);
        text-transform: var(--beam-SelectSubscriptionNonprofit-submitButton-textTransform);
        border-radius: var(--beam-SelectSubscriptionNonprofit-submitButton-borderRadius);
        padding: var(--beam-SelectSubscriptionNonprofit-submitButton-padding);
        transition:
          background-color 0.2s,
          border-color 0.2s,
          color 0.2s;
        cursor: pointer;
        display: inline-flex;
        width: 234.173px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
      }

      .submitButton:not(.isDisabled):focus-visible,
      .submitButton:not(.isDisabled):hover {
        background-color: var(--beam-SelectSubscriptionNonprofit-submitButton-hover-backgroundColor);
        border-color: var(--beam-SelectSubscriptionNonprofit-submitButton-hover-borderColor);
        color: var(--beam-SelectSubscriptionNonprofit-submitButton-hover-color);
      }

      .submitButton.isDisabled {
        color: var(--beam-SelectSubscriptionNonprofit-submitButton-disabled-color);
        background-color: var(--beam-SelectSubscriptionNonprofit-submitButton-disabled-backgroundColor);
        border-color: var(--beam-SelectSubscriptionNonprofit-submitButton-disabled-borderColor);
        cursor: not-allowed;
      }

      .hideStickyFooter {
        display: none;
      }
    `,
    cssCardGrid({
      gap: "var(--beam-SelectSubscriptionNonprofit-options-gap)",
      itemMinWidth: "var(--beam-SelectSubscriptionNonprofit-options-minWidth)",
      columnCount: "var(--beam-SelectSubscriptionNonprofit-options-columnCount)",
    }),
  ];

  protected render() {
    const { selectedNonprofitId } = this;
    const { data, loading } = this.nonprofitListDataController;

    if (loading && !data) {
      return _loading();
    }
    if (this.nonprofitListDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.nonprofitListDataController.error });
      }
      return "";
    }
    if (this.subscriptionUpdateDataController.error) {
      if (this.debug) {
        return _errorMessage({ error: this.subscriptionUpdateDataController.error });
      }
      // do not show error screen for interactive errors by default
    }
    if (data == null) {
      if (this.debug) {
        return _errorMessage({ error: new BeamError("No data") });
      }
      return "";
    }
    const nonprofits = data?.nonprofits || [];
    const selectedNonprofit = nonprofits.find((np) => np.nonprofit.id === selectedNonprofitId) || null;
    // changes images in widget to icons
    const cardStyle = this.cssVariables["--beam-SelectSubscriptionNonprofit-cardStyle"];
    const displayIcon = cardStyle === "'icon'" || cardStyle === "'selected_icon'";

    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <div class="widget-container ${this.isMobile ? "widget-container-mobile" : ""}">
        <div class="header-container ${this.isMobile ? "header-container-mobile" : "header-container-desktop"}">
          <h3 class="title" part="title" id="beam-SelectSubscriptionNonprofit-title">
            ${localizeUserString(this.configLang, data?.config?.web?.title || "") ||
            strings[this.configLang].ctaTitle()}
          </h3>
          <div
            class="description-container ${this.isMobile
              ? "header-container-mobile description-mobile"
              : "header-container-desktop description-desktop"}"
          >
            <span class="description" part="description">
              ${localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["ctaMessage"] || "") ||
              strings[this.configLang].ctaMessage()}
            </span>
            <span class="description-prefix" part="description">
              ${localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["ctaMessagePrefix"] || "") ||
              strings[this.configLang].ctaMessagePrefix()}
            </span>
          </div>
        </div>
        <div class="root">
          <div
            tabindex="-1"
            class="options cardGrid"
            part="options"
            role="radiogroup"
            aria-labelledby="beam-SelectSubscriptionNonprofit-title"
            ?aria-disabled=${!this.allowChange}
          >
            ${repeat(
              nonprofits,
              (i) => i.nonprofit.id,
              (np, index) => {
                const isSelected = selectedNonprofitId === np.nonprofit.id;
                const isFocusable = isSelected || (selectedNonprofit == null && index === 0);
                const unselectedIcon = np.nonprofit.causeIconUrl;
                const selectedIcon = np.nonprofit.causeIconSelectedUrl;
                const cardImage = np.imageUrl;
                const iconImage = cardStyle === "'selected_icon'" ? selectedIcon : unselectedIcon;
                return html`
                  <div
                    class="option ${classMap({
                      isSelected: isSelected,
                      isDisabled: !this.allowChange,
                    })}"
                    part="option"
                    role="radio"
                    tabindex="${isFocusable ? 0 : -1}"
                    data-value=${np.nonprofit.id}
                    ?aria-checked=${isSelected}
                    @click=${this.makeHandleSelect(np.nonprofit.id, index, nonprofits)}
                    @keydown=${this.makeHandleSelect(np.nonprofit.id, index, nonprofits)}
                    aria-label="${localizeUserString(this.configLang, np.nonprofit.cause || "")}"
                  >
                    ${displayIcon
                      ? html` <div class="card-icon-container">
                          <div class="icon-background">
                            <img
                              data-speedsize-ignore="true"
                              src="${iconImage}"
                              alt=""
                              role="presentation"
                              class="card-icon"
                            />
                          </div>
                        </div>`
                      : html` <div class="card-image-container">
                          <img
                            data-speedsize-ignore="true"
                            src="${cardImage}"
                            alt=""
                            role="presentation"
                            class="card-image"
                          />
                        </div>`}
                    <div class="card-details-container" part="details">
                      <div class="details-cause">
                        <span part="checkbox" class="checkbox">
                          <span style="line-height: 0;"> ${_checkbox({ height: "10px", width: "10px" })} </span>
                        </span>
                        ${localizeUserString(this.configLang, np.nonprofit.cause || "")}
                      </div>
                      <p class="details-description">
                        ${unsafeHTML(localizeUserString(this.configLang, np.impact.description || ""))}
                      </p>
                      <div class="card-progress-bar">
                        <beam-progress-bar
                          value="${np.impact.goalProgressPercentage}"
                          style="flex: 1 0;"
                        ></beam-progress-bar>
                        <span class="details-funding-progress-label">
                          ${localizeUserString(this.configLang, np.impact.goalProgressText)}
                        </span>
                      </div>
                    </div>
                  </div>
                `;
              },
            )}
          </div>
        </div>
        <div
          id="footer"
          class="footer ${this.isMobile ? "sticky-footer" : "footer-desktop"} ${this.isMobile &&
          !this.selectedNonprofitId
            ? "hideStickyFooter"
            : ""}"
        >
          <button
            type="button"
            ?aria-disabled=${!this.allowSubmit}
            @click="${this.handleSubmit}"
            class="${classMap({
              isDisabled: !this.allowSubmit,
              submitButton: true,
            })}"
            part="submitButton"
          >
            ${this.subscriptionUpdateDataController.data !== null
              ? localizeUserString(
                  this.configLang,
                  data?.config?.web?.additionalStrings?.["submitButtonTextCompleted"] || "",
                ) || strings[this.configLang].submitButtonTextCompleted()
              : localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["submitButtonText"] || "") ||
                strings[this.configLang].submitButtonText()}
          </button>
        </div>
      </div>
    `;
  }
}

defineCustomElement(BeamSelectSubscriptionNonprofit);

declare global {
  interface HTMLElementTagNameMap {
    "beam-select-subscription-nonprofit": BeamSelectSubscriptionNonprofit;
  }
}
